import { useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useSelector } from 'react-redux'

import {
  getIsLoggedIn,
  getIsRegisteredForOnRamp,
  getUserId,
} from 'store/user/selectors'
import Loader from 'content-components/_Primatives/Loader'
import {
  Wrapper,
  AlreadyRegistered,
  ProblemWithOrder,
} from 'ui-component-library/components/OrderConfirmation'
import LoginOrCreateAccount from './LoginOrCreateAccount'
import Confirm from './Confirm'
import useOnRampOrder from './useOnRampOrder'

const STATES = {
  LOADING: 'loading',
  USER_REQUIRED: 'user required',
  CONFIRM: 'confirm',
  ERROR: 'ERROR',
  ALREADY_REGISTERED: 'already_registered',
  RECLAIM_TOKEN_SENT: 'reclaim_token_sent',
}

const ONRAMP_URL = process.env.ONRAMP_URL || ''

function useQuery<T extends string>(keys: T[]): Record<T, string> {
  const query = new URLSearchParams(useLocation().search)

  return keys.reduce((acc, key) => {
    acc[key] = query.get(key)
    return acc
  }, {} as Record<T, string>)
}

type Props = {
  confirmationMessages: {
    loggedIn: {
      buttonText: string
      titleText: string
      bodyText: string
      waiverLabelText: string
      waiverLink: string
      waiverLinkText: string
      ageConfirmationLabelText: string
    }
    loggedOut: {
      titleText: string
      bodyText: string
    }
  }
  errorMessages: {
    generic: {
      titleText: string
      bodyText: string
    }
  }
}

function OrderConfirmationContainer(props: Props) {
  const { order_id: orderId, claim_token: claimToken } = useQuery([
    'order_id',
    'claim_token',
  ])
  const isLoggedIn = useSelector(getIsLoggedIn)
  const userId = useSelector(getUserId)
  const isRegisteredForOnRamp = useSelector(getIsRegisteredForOnRamp)
  const [error, setError] = useState<string>()

  const {
    claimOrder,
    orderFetched,
    fulfillOrder,
    isFulfilling,
    hasSentReclaimToken,
  } = useOnRampOrder(userId)

  useEffect(() => {
    if (
      typeof orderId === 'string' &&
      userId &&
      !isRegisteredForOnRamp &&
      !orderFetched &&
      !error
    ) {
      claimOrder(orderId, claimToken).catch((e) => setError(e.message))
    }
  }, [orderId, claimToken, userId, orderFetched, error])

  async function handleConfirmSubmit() {
    try {
      await fulfillOrder()
      window.location.replace(ONRAMP_URL)
    } catch (e) {
      setError(e.message)
    }
  }

  const state = useMemo(() => {
    if (error) {
      return STATES.ERROR
    } else if (!isLoggedIn) {
      return STATES.USER_REQUIRED
    } else if (hasSentReclaimToken) {
      return STATES.RECLAIM_TOKEN_SENT
    } else if (isRegisteredForOnRamp) {
      return STATES.ALREADY_REGISTERED
    } else if (orderFetched) {
      return STATES.CONFIRM
    } else {
      return STATES.LOADING
    }
  }, [
    error,
    isLoggedIn,
    hasSentReclaimToken,
    isRegisteredForOnRamp,
    orderFetched,
  ])

  const loginOrCreateAccountStrings = {
    titleText: props.confirmationMessages.loggedOut.titleText,
    bodyText: props.confirmationMessages.loggedOut.bodyText,
  }

  const errorStrings = {
    titleText: props.errorMessages.generic.titleText,
    bodyText: props.errorMessages.generic.bodyText,
  }

  const confirmStrings = {
    buttonText: props.confirmationMessages.loggedIn.buttonText,
    titleText: props.confirmationMessages.loggedIn.titleText,
    bodyText: props.confirmationMessages.loggedIn.bodyText,
    waiverLabelText: props.confirmationMessages.loggedIn.waiverLabelText,
    waiverLink: props.confirmationMessages.loggedIn.waiverLink,
    waiverLinkText: props.confirmationMessages.loggedIn.waiverLinkText,
    ageConfirmationLabelText:
      props.confirmationMessages.loggedIn.ageConfirmationLabelText,
  }

  return (
    <Wrapper>
      {state === STATES.LOADING && <Loader />}
      {state === STATES.USER_REQUIRED && (
        <LoginOrCreateAccount {...loginOrCreateAccountStrings} />
      )}
      {state === STATES.CONFIRM && (
        <Confirm
          {...confirmStrings}
          onSubmit={handleConfirmSubmit}
          isFulfilling={isFulfilling}
        />
      )}
      {state === STATES.ALREADY_REGISTERED && (
        <AlreadyRegistered url={ONRAMP_URL} />
      )}
      {state === STATES.RECLAIM_TOKEN_SENT && (
        <ProblemWithOrder
          {...errorStrings}
          bodyText='Link to claim order is no longer available. Please check your email for new link for registration'
        />
      )}
      {state === STATES.ERROR && (
        <ProblemWithOrder {...errorStrings} errorMessage={error} />
      )}
    </Wrapper>
  )
}

export default OrderConfirmationContainer
